import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { type ColorType, type FontType, useTheme } from '../../../themes'
import type { PlotData, Data } from 'plotly.js'

type Props = {
  plotData: Data[]
  selectedIndex: number | null
  left: number
  top: number
}
// This component is a subcomponent to ConsumerRTHGraph and is not built to be themeable.
function HoverBoxWithRef(
  { plotData, selectedIndex, left, top }: Props,
  ref: React.Ref<HTMLDivElement>,
) {
  const { colors, fonts } = useTheme()

  return (
    <S.HoverBox colors={colors} fonts={fonts} left={left} top={top} ref={ref}>
      {plotData
        .slice(0)
        .reverse()
        .map((series: Data) => {
          const plotSeries = series as PlotData

          if (selectedIndex === null) return ''
          if (
            plotSeries.y[selectedIndex] === null ||
            plotSeries.y[selectedIndex] === 0
          )
            return ''
          if (!plotSeries.marker.color) return ''

          return (
            <S.Row key={plotSeries.name}>
              {
                <S.ColorBox
                  color={
                    plotSeries.marker.color ?
                      plotSeries.marker.color.toString()
                    : ''
                  }
                />
              }
              {plotSeries.name}:{' '}
              <S.Time fonts={fonts}>
                {String(plotSeries.y[selectedIndex]) + ' hours'}
              </S.Time>
            </S.Row>
          )
        })}
      <S.Row key={'FYI'}>*Values may differ from RTH counts</S.Row>
    </S.HoverBox>
  )
}

const HoverBox = forwardRef(HoverBoxWithRef)

export default HoverBox

const S = {
  Time: styled.span.withConfig({
    shouldForwardProp: (prop) => !['fonts'].includes(prop),
  })<{ fonts: FontType }>`
    font-family: ${({ fonts }) => String(fonts.defaultSemibold)};
    font-weight: bold;
  `,
  Row: styled.div`
    margin-bottom: 5px;
    text-align: left;
  `,
  ColorBox: styled.div.withConfig({
    shouldForwardProp: (prop) => !['color'].includes(prop),
  })<{ color: string }>`
    width: 25px;
    height: 10px;
    background-color: ${({ color }) => color};
    display: inline-block;
    margin-right: 5px;
  `,
  HoverBox: styled.div.withConfig({
    shouldForwardProp: (prop) =>
      !['colors', 'fonts', 'top', 'left'].includes(prop),
  })<{ top: number; left: number; colors: ColorType; fonts: FontType }>`
    font-family: ${({ fonts }) => String(fonts.defaultLight)};
    font-size: ${({ fonts }) => String(fonts.defaultSize + 1)}px;
    position: absolute;
    z-index: 1;
    background-color: ${({ colors }) =>
      String(colors.consumerRTH.hoverBoxBackground)};
    border: 1px solid ${({ colors }) => String(colors.consumerRTH.hoverBox)};
    padding: 5px;
    top: ${({ top }) => top}px;
    left: ${({ left }) => left}px;
  `,
}
